import type {
  SurveyAnswerInput, SurveyFrequency, SurveyOption, SurveyQuestion, SurveyQuestionType, SurveyResponseMode, SurveySection
} from '@/types/surveys'

/** API limits (PROPOSTA-PESQUISAS §5b): above them the API answers 422. */
export const MAX_SECTIONS = 20
export const MAX_QUESTIONS = 100
export const MIN_ANONYMOUS = 3

export const QUESTION_TYPES: SurveyQuestionType[] = ['SINGLE_CHOICE', 'MULTIPLE_CHOICE', 'SCALE', 'NPS', 'TEXT']

let counter = 0
/** A key for the editor only (React lists and dnd-kit ids); never sent. */
export const newKey = (prefix: string): string => `${prefix}${Date.now().toString(36)}${(counter++).toString(36)}`

export interface EditorOption extends SurveyOption {
  key: string
}

export interface EditorQuestion extends Omit<SurveyQuestion, 'options'> {
  key: string
  options: EditorOption[] | null
}

export interface EditorSection {
  key: string
  title: string
  description: string | null
  questions: EditorQuestion[]
}

const toEditorOption = (o: SurveyOption): EditorOption => ({ key: newKey('o'), value: o.value, label: o.label })

export const toEditorQuestion = (q: SurveyQuestion): EditorQuestion => ({
  ...q,
  key: newKey('q'),
  options: q.options ? q.options.map(toEditorOption) : null
})

export const toEditorSections = (sections: SurveySection[] | undefined): EditorSection[] =>
  (sections || []).map((s) => ({ key: newKey('s'), title: s.title, description: s.description, questions: s.questions.map(toEditorQuestion) }))

/** The tree as the API takes it (the whole of it, on every save of a draft). */
export const toApiSections = (sections: EditorSection[]): SurveySection[] => sections.map((s) => ({
  title: s.title.trim(),
  description: s.description?.trim() || null,
  questions: s.questions.map((q) => ({
    type: q.type,
    title: q.title.trim(),
    help: q.help?.trim() || null,
    required: q.required,
    options: q.options ? q.options.map((o) => (o.value ? { value: o.value, label: o.label.trim() } : { label: o.label.trim() } as SurveyOption)) : null,
    min: q.min,
    max: q.max,
    minLabel: q.minLabel?.trim() || null,
    maxLabel: q.maxLabel?.trim() || null,
    display: q.display
  }))
}))

/** A new question of a type, with placeholders the person types over. */
export const emptyQuestion = (type: SurveyQuestionType, labels: { option: (n: number) => string }): EditorQuestion => {
  const base: EditorQuestion = {
    key: newKey('q'), type, title: '', help: null, required: false, options: null,
    min: null, max: null, minLabel: null, maxLabel: null, display: null
  }
  if (type === 'SINGLE_CHOICE' || type === 'MULTIPLE_CHOICE') {
    base.options = [1, 2, 3].map((n) => ({ key: newKey('o'), value: '', label: labels.option(n) }))
  } else if (type === 'SCALE') {
    Object.assign(base, { min: 1, max: 5, display: 'numbers' })
  } else if (type === 'NPS') {
    Object.assign(base, { min: 0, max: 10 })
  } else {
    base.display = 'short'
  }
  return base
}

/** Duplicate keeps the options (the APICON rating repeats in 9 questions); fresh keys, no server value. */
export const duplicateQuestion = (q: EditorQuestion): EditorQuestion => ({
  ...q,
  key: newKey('q'),
  options: q.options ? q.options.map((o) => ({ ...o, key: newKey('o') })) : null
})

/** Changing the type keeps the title and resets what belongs to the old type. */
export const retype = (q: EditorQuestion, type: SurveyQuestionType, labels: { option: (n: number) => string }): EditorQuestion => {
  const fresh = emptyQuestion(type, labels)
  const choice = (t: SurveyQuestionType) => t === 'SINGLE_CHOICE' || t === 'MULTIPLE_CHOICE'
  return {
    ...fresh,
    key: q.key,
    title: q.title,
    help: q.help,
    required: q.required,
    options: choice(type) && choice(q.type) ? q.options : fresh.options
  }
}

export const questionCount = (sections: EditorSection[]): number => sections.reduce((n, s) => n + s.questions.length, 0)

// ---------- answering ----------

export type AnswerValue = { optionValues?: string[]; number?: number | null; text?: string }
export type AnswerMap = Record<string, AnswerValue>

export const isAnswered = (q: SurveyQuestion, a: AnswerValue | undefined): boolean => {
  if (!a) return false
  if (q.type === 'SINGLE_CHOICE' || q.type === 'MULTIPLE_CHOICE') return (a.optionValues?.length ?? 0) > 0
  if (q.type === 'SCALE' || q.type === 'NPS') return Number.isInteger(a.number)
  return !!a.text?.trim()
}

/** The keys of the questions of a step that are required and still empty. */
export const missingRequired = (section: SurveySection, answers: AnswerMap): string[] =>
  section.questions.filter((q) => q.required && q.id && !isAnswered(q, answers[q.id])).map((q) => q.id as string)

export const toAnswerInputs = (sections: SurveySection[], answers: AnswerMap): SurveyAnswerInput[] => {
  const out: SurveyAnswerInput[] = []
  for (const q of sections.flatMap((s) => s.questions)) {
    const a = q.id ? answers[q.id] : undefined
    if (!q.id || !a || !isAnswered(q, a)) continue
    if (q.type === 'SINGLE_CHOICE' || q.type === 'MULTIPLE_CHOICE') out.push({ questionId: q.id, optionValues: a.optionValues })
    else if (q.type === 'SCALE' || q.type === 'NPS') out.push({ questionId: q.id, number: a.number })
    else out.push({ questionId: q.id, text: (a.text ?? '').trim() })
  }
  return out
}

/**
 * The preview walks the editor's tree, which has no ids yet: it borrows the
 * editor keys as ids so the same screen can run without saving anything.
 */
export const previewSections = (sections: EditorSection[]): SurveySection[] => sections.map((s) => ({
  id: s.key,
  title: s.title,
  description: s.description,
  questions: s.questions.map((q) => ({
    ...q,
    id: q.key,
    options: q.options ? q.options.map((o) => ({ value: o.value || o.key, label: o.label })) : null
  }))
}))

// ---------- the deadline, in the community's time zone ----------

/** `YYYY-MM-DDTHH:mm` of an instant in a time zone (the value of a datetime-local input). */
export const toZonedInput = (iso: string | null | undefined, timeZone: string | undefined): string => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  try {
    const p = Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
      timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
    }).formatToParts(d).map((x) => [x.type, x.value]))
    return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`
  } catch {
    return d.toISOString().slice(0, 16)
  }
}

/** The instant a wall-clock time in a time zone stands for (two passes cover a DST edge). */
export const fromZonedInput = (local: string, timeZone: string | undefined): string | null => {
  const m = local.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/)
  if (!m) return null
  const wall = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5])
  let guess = wall
  for (let i = 0; i < 2; i += 1) {
    const shown = toZonedInput(new Date(guess).toISOString(), timeZone)
    const s = shown.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/)
    if (!s) break
    guess += wall - Date.UTC(+s[1], +s[2] - 1, +s[3], +s[4], +s[5])
  }
  return new Date(guess).toISOString()
}

// ---------- API errors ----------

/** The axios-like error the SDK throws (`internal.ts`). */
export interface ApiError {
  response?: { status?: number; data?: { message?: string; errors?: { param?: string; message: string }[] } }
}
export const errStatus = (e: unknown): number | undefined => (e as ApiError | null)?.response?.status
export const errMessage = (e: unknown, fallback: string): string => (e as ApiError | null)?.response?.data?.message || fallback
export const errList = (e: unknown): { param?: string; message: string }[] => {
  const list = (e as ApiError | null)?.response?.data?.errors
  return Array.isArray(list) ? list : []
}

/** "Pesquisa opcional / obrigatória / Resposta imediata", in that order. */
export const RESPONSE_MODES: SurveyResponseMode[] = ['optional', 'required', 'immediate']

/**
 * F-137 criterion 8: the heat map on an ANONYMOUS survey (and the "group by
 * area" switch that feeds it) waits for the security verdict. Flip this one
 * constant to turn both on; identified surveys show the map regardless.
 */
export const ANONYMOUS_HEATMAP_ENABLED = false

export const FREQUENCIES: SurveyFrequency[] = ['weekly', 'biweekly', 'monthly', 'quarterly']
/** The API's longest round per frequency (422 `recurrenceDurationTooLong` above it). */
export const MAX_ROUND_DAYS: Record<SurveyFrequency, number> = { weekly: 7, biweekly: 14, monthly: 28, quarterly: 90 }
export const isWeekly = (f: SurveyFrequency): boolean => f === 'weekly' || f === 'biweekly'

/** Saves a CSV text as a file. */
export const downloadCsv = (text: string, name: string) => {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }))
  const a = document.createElement('a')
  a.href = url
  a.download = name
  a.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/** Words that promise anonymity, in the app's languages (anônima, anonymous, anónima, anonima, anonym). */
const ANONYMITY_WORDS = /an[oôó]nim|anonym/i

/** Whether an intro (HTML) talks about anonymity. */
export const introMentionsAnonymity = (html: string | null | undefined) => ANONYMITY_WORDS.test((html || '').replace(/<[^>]*>/g, ' '))

/** The intro without the paragraphs (or lines) that promise anonymity. */
export const stripAnonymityFromIntro = (html: string): string => {
  const blocks = html.match(/<(p|li|h[1-6])[^>]*>[\s\S]*?<\/\1>/gi)
  if (!blocks) return ANONYMITY_WORDS.test(html) ? '' : html
  return blocks.filter((b) => !ANONYMITY_WORDS.test(b.replace(/<[^>]*>/g, ' '))).join('')
}
